import React, { useEffect, useRef, useState } from 'react';
import { Columns3, Check } from 'lucide-react';

/**
 * Dropdown para elegir qué columnas se muestran en una tabla.
 * - `columns`: [{ key, label, always? }]
 * - `value`: Set<string> con las keys visibles
 * - `onChange(next: Set<string>)`
 * - `storageKey` (opcional): persiste en localStorage.
 * Las columnas marcadas `always` no se pueden desmarcar.
 */
export const ColumnPicker = ({ columns, value, onChange, storageKey }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const toggle = (key, col) => {
    if (col.always) return;
    const next = new Set(value);
    if (next.has(key)) next.delete(key); else next.add(key);
    onChange(next);
    if (storageKey) {
      try { localStorage.setItem(storageKey, JSON.stringify(Array.from(next))); } catch (_) {}
    }
  };

  const shown = columns.filter((c) => value.has(c.key)).length;

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="column-picker-btn"
        className="flex items-center gap-1.5 bg-black/40 border border-white/10 rounded-lg px-2.5 py-1.5 text-[11px] font-bold text-white/80 hover:border-[#A3FF12]/40 hover:text-white"
      >
        <Columns3 size={13}/>
        Columnas <span className="text-white/40 font-mono">({shown}/{columns.length})</span>
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 z-40 bg-[#0a0a0a] border border-white/15 rounded-xl shadow-2xl p-1.5 w-56 max-h-80 overflow-auto"
             data-testid="column-picker-menu">
          <div className="text-[10px] uppercase tracking-widest text-white/40 px-2 py-1">Datos visibles</div>
          {columns.map((c) => {
            const checked = value.has(c.key);
            return (
              <button
                key={c.key}
                onClick={() => toggle(c.key, c)}
                disabled={c.always}
                data-testid={`column-picker-${c.key}`}
                className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs text-left ${
                  c.always ? 'opacity-60 cursor-default'
                    : 'hover:bg-white/5 cursor-pointer'
                } ${checked ? 'text-white' : 'text-white/50'}`}
              >
                <span className={`w-4 h-4 rounded border flex items-center justify-center ${
                  checked ? 'bg-[#A3FF12] border-[#A3FF12]' : 'border-white/30'
                }`}>
                  {checked && <Check size={11} className="text-black"/>}
                </span>
                <span className="flex-1">{c.label}</span>
                {c.always && <span className="text-[9px] text-white/30 uppercase">fija</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

/** Hook para gestionar el estado con persistencia en localStorage. */
export const useVisibleColumns = (columns, storageKey) => {
  const [value, setValue] = useState(() => {
    if (storageKey) {
      try {
        const raw = localStorage.getItem(storageKey);
        if (raw) {
          const arr = JSON.parse(raw);
          if (Array.isArray(arr)) {
            const s = new Set(arr);
            // Aseguramos las always-visible
            columns.forEach((c) => { if (c.always) s.add(c.key); });
            return s;
          }
        }
      } catch (_) {}
    }
    return new Set(columns.filter((c) => c.defaultVisible !== false).map((c) => c.key));
  });
  return [value, setValue];
};
